import { checkClaimsWeb } from '@eventr/common'
import {
    ColoursObj,
    ErrorWrapper,
    Gutters,
    ImageRenderer,
    MainButton,
    MobileConfirmDialog,
    TDialogStore,
    TImageVariants,
    Typography,
    useDialogStore,
} from '@eventr/common-storybook'
import { Box, Stack } from '@mui/material'
import { useShallow } from 'zustand/react/shallow'
import { HeaderSearchBar } from '../../../home/components/carousel/slide/footer/HeaderSearchBar'
import i18n from '../../../i18n/i18next'
import { useBoundStore } from '../../../state/bound.store'
import { query } from '../../../state/headerState/headerSignals'
import { selectCurrentItineraryTitle } from '../../../state/itineraryState/currentItin.slice'
import { useThemeContext } from '../../../state/themeState/themeContext'
import { ScreenTypeProvider } from '../../../v2/state/screenTypeProvider'
import { NavigationView } from '../../layout/NavigationView'

const EmptyBookings = ({ section, src }: { section: string; src: string }) => {
    return <ImageRenderer variant={section as TImageVariants} src={src} />
}
interface IBaseListPage {
    itemCount: number
    children: React.ReactNode
    sectionName: string
    deleteAllitems?: () => void
    addItem?: () => void
}
export const BaseListPage = ({
    itemCount,
    children,
    sectionName,
    deleteAllitems,
    addItem,
}: IBaseListPage) => {
    const themeImages = useThemeContext((s) => s.themeImages)

    const { selectedItinID } = useBoundStore(
        useShallow((s) => ({
            selectedItinID: s.selectedItinID,
        }))
    )
    const itineraryTitle = useBoundStore(selectCurrentItineraryTitle)
    const { replaceDialog, clearDialog }: TDialogStore = useDialogStore(
        useShallow((s: TDialogStore) => ({
            clearDialog: s.clearDialog,
            replaceDialog: s.replaceDialog,
        }))
    )

    const onBackPressed = () => {
        query.value = ''
        window.goBack()
    }

    return (
        <>
            <NavigationView
                usePageTransition
                navigationBarTitle={
                    i18n.t('edit-itinerary.section.edit-section', {
                        sectionName: i18n.t(
                            `sections.${sectionName.toLowerCase()}`,
                            {
                                count: 0,
                            }
                        ),
                    }) ?? 'Edit or Add Items'
                }
                navigationBarSubtitle={itineraryTitle}
                backButtonAction={onBackPressed}
                navigationBarTrailing={[
                    {
                        id: 'moreButtonEditSection',
                        icon: 'More',
                        popoverItems: [
                            {
                                title: i18n.t(
                                    'edit-itinerary.section.delete-all',
                                    {
                                        sectionName: i18n.t(
                                            `sections.${sectionName.toLowerCase()}`,
                                            { count: 0 }
                                        ),
                                    }
                                ),
                                action: () => {
                                    replaceDialog({
                                        node: (
                                            <MobileConfirmDialog
                                                header={i18n.t(
                                                    'edit-itinerary.section.delete-all',
                                                    {
                                                        sectionName: i18n.t(
                                                            `sections.${sectionName.toLowerCase()}`,
                                                            { count: 0 }
                                                        ),
                                                    }
                                                )}
                                                text={i18n.t(
                                                    'edit-itinerary.section.delete-all-check',
                                                    {
                                                        sectionName: i18n.t(
                                                            `sections.${sectionName.toLowerCase()}`,
                                                            { count: 0 }
                                                        ),
                                                    }
                                                )}
                                                onConfirm={() => {
                                                    deleteAllitems()
                                                    clearDialog()
                                                }}
                                                onCancel={() => {
                                                    clearDialog()
                                                }}
                                                cancelText={i18n.t(
                                                    'general.cancel'
                                                )}
                                                confirmText={i18n.t(
                                                    'general.delete'
                                                )}
                                                confirmBtnStyle="destructiveBorderless"
                                            />
                                        ),
                                        backdropClosable: false,
                                    })
                                },
                                color: ColoursObj.states.error,
                                active:
                                    itemCount > 0 &&
                                    checkClaimsWeb(
                                        [`/${selectedItinID}`],
                                        ['deleteItinerary']
                                    ),
                            },
                        ],
                    },
                ]}
            >
                <Stack>
                    <>
                        <Gutters>
                            <Box display={'grid'} gap={'8px'}>
                                {itemCount > 0 && <HeaderSearchBar />}

                                <MainButton
                                    id={`Add ${sectionName}-button`}
                                    text={
                                        i18n.t(
                                            'edit-itinerary.section.add-new',
                                            {
                                                sectionName: i18n.t(
                                                    `sections.${sectionName.toLowerCase()}`,
                                                    { count: 1 }
                                                ),
                                            }
                                        ) ?? 'Add New'
                                    }
                                    onClick={addItem}
                                    icon="Add"
                                    iconPosition="left"
                                    style={{
                                        boxShadow:
                                            '0px 4px 8px 0px rgba(51, 82, 111, 0.10)',
                                    }}
                                />
                            </Box>
                        </Gutters>

                        {itemCount === 0 ? (
                            <Stack alignItems="center">
                                <EmptyBookings
                                    section={sectionName}
                                    src={themeImages[sectionName]}
                                />
                                <Typography
                                    align="center"
                                    variant="bodyBold"
                                    color={ColoursObj.core.primary}
                                >
                                    {i18n.t(
                                        'edit-itinerary.section.empty-message',
                                        {
                                            count: 0,
                                            sectionName: i18n.t(
                                                `sections.${sectionName}`,
                                                { count: 0 }
                                            ),
                                        }
                                    )}
                                </Typography>
                            </Stack>
                        ) : null}

                        <Stack marginX="8px">
                            <ErrorWrapper>
                                <ScreenTypeProvider screenType="edit">
                                    {children}
                                </ScreenTypeProvider>
                            </ErrorWrapper>
                        </Stack>
                    </>

                    <div />
                </Stack>
            </NavigationView>
        </>
    )
}
